<template>
  <div
    class="revision-list-item d-flex ga-1 align-start"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <slot name="before-revision-item" />

    <!-- Content -->
    <div class="revision-content d-flex flex-column ga-1 flex-grow-1">
      <!-- Badges -->
      <div
        class="d-flex ga-2 flex-wrap"
      >
        <!-- Quarter badge -->
        <span
          v-if="revision.quarter"
          class="revision-badge revision-badge--quarter d-inline-flex align-center justify-center text-caption"
        >
          {{ revision.quarter }}
        </span>
        <!-- Finalized badge -->
        <span
          v-if="revision.is_finalized"
          class="revision-badge revision-badge--finalized d-inline-flex align-center justify-center text-caption text-textLight"
        >
          Published
        </span>
        <span
          v-if="revision.isSynced"
          class="revision-badge d-inline-flex align-center justify-center text-caption text-textLight"
        >
          Synced with ATS
        </span>
        <span
          v-if="revision.isDatapeopleAnywhere"
          class="revision-badge revision-badge--dpa d-inline-flex align-center justify-center text-caption"
        >
          JobNav Anywhere
        </span>
        <!-- Current version badge -->
        <span
          v-if="revision.index === 0"
          class="current-version-badge mb-1 text-info text-caption align-self-start rounded-pill"
        >
          Current version
        </span>
        <span
          v-if="revision.revision_name || (isHovered && isEditable)"
          class="named-revision-badge text-success text-caption align-self-start rounded-pill d-inline-flex align-center ga-1"
          :class="{ 'named-revision-badge--editing': isEditing }"
          @click.stop
        >
          <template v-if="!isEditing">
            {{ revision.revision_name }}
            <v-icon
              v-if="isHovered && isEditable"
              class="edit-icon"
              size="12"
              @click.stop="startEdit"
            >
              mdi-pencil
            </v-icon>
          </template>
          <template v-else>
            <span
              ref="namePill"
              class="name-input"
              contenteditable="true"
              @keydown.enter.prevent="commitEdit"
            />
            <v-icon
              class="edit-icon ml-auto"
              size="12"
              @mousedown.prevent="commitEdit"
            >
              mdi-check
            </v-icon>
          </template>
        </span>
      </div>

      <div class="d-flex ga-2 flex-wrap">
        <!-- Date and Time -->
        <div class="d-flex align-center ga-1 flex-wrap">
          <span class="text-body-2 text-textDark">{{ formattedDate }}</span>
          <span class="text-caption text-textLight">·</span>
          <span class="text-caption text-textLight">{{ formattedTime }}</span>
        </div>

        <!-- Creator -->
        <div
          v-if="revision.createdBy"
          class="text-body-2 text-textLight text-truncate"
        >
          by {{ revision.createdBy }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, ref } from 'vue';

const props = defineProps({
  revision: {
    type: Object,
    required: true,
  },
  isEditable: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const emit = defineEmits(['revision-name-edit']);

const isHovered = ref(false);
const isEditing = ref(false);
const namePill = ref(null);

const startEdit = async () => {
  if (!props.isEditable) return;
  isEditing.value = true;
  await nextTick();
  if (namePill.value) {
    namePill.value.innerText = props.revision.revision_name || '';
    namePill.value.focus();
    // Place cursor at end
    const range = document.createRange();
    range.selectNodeContents(namePill.value);
    range.collapse(false);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  }
};

const commitEdit = () => {
  if (!props.isEditable || !isEditing.value) return;
  isEditing.value = false;
  const name = namePill.value?.innerText?.trim() ?? '';
  emit('revision-name-edit', { revisionUlid: props.revision.ulid, name });
};

// Parse the created date string to extract date and time
const formattedDate = computed(() => {
  if (!props.revision.created) return '';

  // Job Post revision format: "Mar 21, 2025 12:45PM"
  const parts = props.revision.created.split(' ');
  if (parts.length >= 3) {
    // Return "Mar 21, 2025"
    return `${parts[0]} ${parts[1]} ${parts[2]}`;
  }

  // Job Profile revision
  const date = new Date(props.revision.created);
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
});

const formattedTime = computed(() => {
  if (!props.revision.created) return '';

  // Job Post revision format: "Mar 21, 2025 12:45PM"
  const parts = props.revision.created.split(' ');
  if (parts.length >= 4) {
    // Return "12:45PM" formatted as "12:45 PM"
    const time = parts[3];
    const match = time.match(/^(\d{1,2}):(\d{2})(AM|PM)$/i);
    if (match) {
      return `${match[1]}:${match[2]} ${match[3]}`;
    }
    return time;
  }

  // Job Profile revision
  const date = new Date(props.revision.created);
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
});
</script>

<style lang="less" scoped>
.revision-content {
  min-width: 0; // Allows flex items to shrink below content size
}

.revision-badge {
  padding: 2px 8px;
  background-color: @grey-400;
  border-radius: 24px;

  &--dpa {
    background-color: @purple-100;
    color: #E100FF;
  }

  &--quarter {
    background-color: @orange-100;
    color: @orange-500;
  }

  &--finalized {
    background-color: @green-200;
    color: @green-500;
  }
}
.current-version-badge {
  padding: 2px 6px;
  background-color: @bg-info-light;
}
.named-revision-badge {
  padding: 2px 6px;
  background-color: @bg-success-light;
  cursor: default;
  white-space: nowrap;

  &--editing {
    outline: 1px solid @green-500;
    cursor: text;
    min-width: 32px;
    min-height: 10px;
  }

  .name-input {
    outline: none;
    min-width: 32px;
    flex: 1;
  }

  .edit-icon {
    cursor: pointer;
    opacity: 0.7;

    &:hover {
      opacity: 1;
    }
  }
}
</style>
